import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, MoreVertical, X } from "lucide-react";
import DatePicker from "../../../Common/DatePicker";
import { attendanceService, authService } from "../../../../services/api";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import {
  buildMonthPolicyStatusMap,
  loadAttendanceShifts,
} from "../../../../lib/attendancePolicyEngine";
import {
  getShiftOverrideForDay,
  setShiftOverrideForDay,
  clearShiftOverrideForDay,
  SHIFT_OVERRIDE_UPDATED_EVENT,
} from "../../../../lib/attendanceShiftOverride";
import toast from "react-hot-toast";
import {
  ATTENDANCE_POLICY_UPDATED_EVENT,
  ensureDemoAttendancePolicy,
  getDemoPolicyTestGuide,
} from "../../../../lib/attendanceDemoSeed";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  readEmployeeWorklog,
  readEmployeeWorklogMap,
} from "../../../../lib/attendancePunchStorage";
import { ATTENDANCE_SCHEDULE_RECALC_EVENT } from "../../../../lib/attendanceGeneralSettings";
import { clampDateIsoToToday, isFutureDateIso, regularisedChecksSessionKey } from "../../../../lib/attendanceDayChecks";
import RegulariseChecksRequestModal from "./RegulariseChecksRequestModal";

const getMonthRangeValue = (year, month) => {
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  const endDay = new Date(year, month + 1, 0).getDate().toString().padStart(2, "0");
  return `${year}-${paddedMonth}-01/${year}-${paddedMonth}-${endDay}`;
};

const combineStatuses = (firstHalfStatus, secondHalfStatus) => {
  if (firstHalfStatus === secondHalfStatus) return firstHalfStatus;
  return `${firstHalfStatus}/${secondHalfStatus}`;
};

const normalizeStatus = (status) => {
  if (status === "LWP") return "LOP";
  return status;
};

/** Map canonical codes to company display codes (matches Attendance grid). */
const mapStatusForDisplay = (raw, attendanceMapping) => {
  if (!raw || typeof raw !== "string") return raw;
  const s = raw.trim();
  if (!s) return raw;
  if (s.includes("/")) {
    const [a, b] = s.split("/");
    const ma =
      attendanceMapping?.[String(a).toUpperCase().trim()] || String(a).trim();
    const mb =
      attendanceMapping?.[String(b).toUpperCase().trim()] || String(b).trim();
    return `${ma}/${mb}`;
  }
  const lookup = s.toUpperCase();
  return attendanceMapping?.[lookup] || s;
};

const splitStatusParts = (displayStr) => {
  if (!displayStr || typeof displayStr !== "string") return null;
  const s = displayStr.trim();
  if (s.includes("/")) {
    const p = s.split("/");
    if (p.length === 2 && p[0] && p[1]) return [p[0].trim(), p[1].trim()];
  }
  return null;
};

/**
 * Color rules aligned with AttendanceTable / AttendanceCell in Attendance.jsx
 * (custom colors from leave types + heuristic fallbacks).
 */
const getStatusColorPresentation = (code, codeColorMap, darkMode) => {
  if (!code || typeof code !== "string") {
    return {
      className: darkMode ? "text-gray-400" : "text-gray-700",
      style: {},
    };
  }
  const norm = normalizeStatus(code).toUpperCase();
  if (codeColorMap && codeColorMap[norm]) {
    const color = codeColorMap[norm];
    if (
      color &&
      (color.startsWith("#") || color.startsWith("rgb") || /^[a-zA-Z]+$/.test(color))
    ) {
      return { className: "font-bold", style: { color } };
    }
    return { className: `${color} font-bold`, style: {} };
  }
  if (
    norm.includes("LOP") ||
    norm.includes("LWP") ||
    norm.includes("LO") ||
    norm.includes("LW")
  ) {
    return {
      className: darkMode ? "text-red-400 font-bold" : "text-red-700 font-bold",
      style: {},
    };
  }
  const light = {
    PR: "text-emerald-700",
    PH: "text-amber-700",
    WO: "text-redhalo-navy",
    LOP: "text-red-700",
  };
  const dark = {
    PR: "text-emerald-400",
    PH: "text-amber-400",
    WO: "text-cyan-300",
    LOP: "text-red-400",
  };
  const palette = darkMode ? dark : light;
  return {
    className: `${palette[norm] || (darkMode ? "text-gray-300" : "text-gray-700")} font-bold`,
    style: {},
  };
};

const ATTENDANCE_SHIFTS_STORAGE_KEY = "hrms_frontend_attendance_shifts_v1";
const POLICY_REFRESH_MS = 60000;
const QUICK_ACTION_REQUESTS_STORAGE_KEY = "adminQuickActionRequests";

const formatClockFromMs = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatElapsedHMS = (startMs, endMs) => {
  if (
    startMs == null ||
    endMs == null ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs)
  ) {
    return "--";
  }
  const diff = Math.max(0, endMs - startMs);
  const totalSec = Math.floor(diff / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const resolveDefaultShiftName = () => {
  try {
    const raw = localStorage.getItem(ATTENDANCE_SHIFTS_STORAGE_KEY);
    if (!raw) return "General";
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return "General";
    const defaultShift = parsed.find((s) => s && s.isDefault);
    if (defaultShift?.name) return String(defaultShift.name).trim();
    const generalShift = parsed.find(
      (s) => s && String(s.name || "").trim().toLowerCase() === "general"
    );
    if (generalShift?.name) return String(generalShift.name).trim();
    return "General";
  } catch {
    return "General";
  }
};

const resolveApprovedChangeShiftName = (employeeId, selectedDateIso) => {
  if (!employeeId || !selectedDateIso) return null;
  try {
    const raw = localStorage.getItem(QUICK_ACTION_REQUESTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const targetEmpId = Number(employeeId);
    const approvedShiftRequests = parsed
      .filter((r) => {
        if (!r || r.actionLabel !== "Change Shift" || r.status !== "Approved") return false;
        const reqEmpId = Number(r.employeeId);
        if (!Number.isFinite(reqEmpId) || !Number.isFinite(targetEmpId) || reqEmpId !== targetEmpId) {
          return false;
        }
        const effectiveDate = String(r?.formData?.date || "").trim();
        const shiftName = String(r?.formData?.shift || "").trim();
        if (!effectiveDate || !shiftName) return false;
        return effectiveDate <= selectedDateIso;
      })
      .sort((a, b) => {
        const ad = String(a?.formData?.date || "");
        const bd = String(b?.formData?.date || "");
        if (ad !== bd) return bd.localeCompare(ad);
        return Number(b?.submittedAt || 0) - Number(a?.submittedAt || 0);
      });

    if (approvedShiftRequests.length === 0) return null;
    return String(approvedShiftRequests[0]?.formData?.shift || "").trim() || null;
  } catch {
    return null;
  }
};

const getDefaultSelectedDay = (year, month) => {
  const now = new Date();
  if (year === now.getFullYear() && month === now.getMonth()) {
    return now.getDate();
  }
  return null;
};

const renderColoredStatusContent = (
  raw,
  attendanceMapping,
  codeColorMap,
  darkMode,
  textSizeClass = "text-[9.5px]"
) => {
  if (!raw || raw === "-") {
    return (
      <span
        className={`${textSizeClass} font-bold ${
          darkMode ? "text-gray-500" : "text-gray-400"
        }`}
      >
        -
      </span>
    );
  }
  const mapped = mapStatusForDisplay(String(raw), attendanceMapping);
  const parts = splitStatusParts(mapped);
  if (parts) {
    return (
      <div className="flex flex-col items-center justify-center gap-0 leading-tight">
        {parts.map((p, i) => {
          const pres = getStatusColorPresentation(p, codeColorMap, darkMode);
          return (
            <span
              key={i}
              className={`${textSizeClass} ${pres.className}`}
              style={pres.style}
            >
              {p}
            </span>
          );
        })}
      </div>
    );
  }
  const pres = getStatusColorPresentation(mapped, codeColorMap, darkMode);
  return (
    <span className={`${textSizeClass} ${pres.className}`} style={pres.style}>
      {mapped}
    </span>
  );
};

const EmployeeAttendanceCalendarPopup = ({
  darkMode,
  isOpen,
  onClose,
  embedded = false,
  employee,
  selectedMonth,
  selectedYear,
  attendanceData = {},
  attendanceDetailData = {},
  leaveTypes = [],
  isLocked = false,
  onApplyLeave,
  onEditAttendanceStatus,
  onSaveRegularizationTimes,
}) => {
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canApplyLeave = isAdmin || hasScreenAccess("ess_quick_panel", "leave_request", "view");
  const canRegularise = isAdmin || hasScreenAccess("ess_quick_panel", "regularization_checks", "view");
  const [selectedDay, setSelectedDay] = useState(null);
  const [localMonth, setLocalMonth] = useState(selectedMonth);
  const [localYear, setLocalYear] = useState(selectedYear);
  const [localAttendanceMap, setLocalAttendanceMap] = useState({});
  const [localAttendanceDetailMap, setLocalAttendanceDetailMap] = useState({});
  const [isLoadingMonthData, setIsLoadingMonthData] = useState(false);
  const [showDayActionMenu, setShowDayActionMenu] = useState(false);
  const [showStatusEditModal, setShowStatusEditModal] = useState(false);
  const [firstHalfStatus, setFirstHalfStatus] = useState("PR");
  const [secondHalfStatus, setSecondHalfStatus] = useState("PR");
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [regularisationSessionLog, setRegularisationSessionLog] = useState([]);
  const [policyRefreshTick, setPolicyRefreshTick] = useState(0);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [dayShiftOverrideId, setDayShiftOverrideId] = useState("");
  const [loggedInEmployeeId, setLoggedInEmployeeId] = useState(null);
  const [demoPolicyReady, setDemoPolicyReady] = useState(false);

  const regLogStorageKey = useMemo(
    () => regularisedChecksSessionKey(employee?.id),
    [employee?.id]
  );

  const demoTestGuide = useMemo(() => getDemoPolicyTestGuide(), []);

  const reloadRegularisationSessionLog = () => {
    if (!regLogStorageKey) return;
    try {
      const raw = sessionStorage.getItem(regLogStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRegularisationSessionLog(parsed);
      } else {
        setRegularisationSessionLog([]);
      }
    } catch {
      setRegularisationSessionLog([]);
    }
  };

  useEffect(() => {
    if (!isOpen || !regLogStorageKey) return;
    reloadRegularisationSessionLog();
  }, [isOpen, regLogStorageKey]);

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    const onHistory = (e) => {
      const id = String(e?.detail?.employeeId ?? "");
      if (!id || id === String(employee.id)) reloadRegularisationSessionLog();
    };
    window.addEventListener("hrms-regularisation-history-updated", onHistory);
    return () => window.removeEventListener("hrms-regularisation-history-updated", onHistory);
  }, [isOpen, employee?.id, regLogStorageKey]);

  useEffect(() => {
    if (!isOpen) return;
    ensureDemoAttendancePolicy();
    setDemoPolicyReady(true);
    let cancelled = false;
    authService
      .getCurrentUser()
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.employeeId ?? res?.data?.employee?.id ?? null;
        setLoggedInEmployeeId(id != null ? String(id) : null);
      })
      .catch(() => {
        if (!cancelled) setLoggedInEmployeeId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /** Refresh PR/LOP when punches change, policy changes, or on interval. */
  useEffect(() => {
    if (!isOpen) return;
    const bump = () => setPolicyRefreshTick((n) => n + 1);
    const onPunch = (e) => {
      const targetId = String(employee?.id ?? "");
      const punchEmp = String(e?.detail?.employeeId ?? "");
      if (!targetId || punchEmp === targetId) bump();
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.addEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, bump);
    window.addEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
    window.addEventListener(SHIFT_OVERRIDE_UPDATED_EVENT, bump);
    const intervalId = window.setInterval(bump, POLICY_REFRESH_MS);
    return () => {
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
      window.removeEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, bump);
      window.removeEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
      window.removeEventListener(SHIFT_OVERRIDE_UPDATED_EVENT, bump);
      window.clearInterval(intervalId);
    };
  }, [isOpen, employee?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setShiftOptions(loadAttendanceShifts().filter((s) => s.status !== "inactive"));
  }, [isOpen]);

  /** Reset calendar UI when month/employee changes (history lives in RegulariseChecksRequestModal). */
  useEffect(() => {
    if (!isOpen) return;
    const now = new Date();
    let y = selectedYear;
    let mo = selectedMonth;
    if (y > now.getFullYear() || (y === now.getFullYear() && mo > now.getMonth())) {
      y = now.getFullYear();
      mo = now.getMonth();
    }
    setSelectedDay(getDefaultSelectedDay(y, mo));
    setLocalMonth(mo);
    setLocalYear(y);
    setShowDayActionMenu(false);
    setShowRegularizationModal(false);
  }, [isOpen, selectedMonth, selectedYear, employee?.id]);

  const daysInMonth = useMemo(
    () => Math.min(31, new Date(localYear, localMonth + 1, 0).getDate()),
    [localYear, localMonth]
  );

  /** JS getDay() is Sun=0; shift so week starts Monday (Mon=0 … Sun=6). */
  const dayOffset = useMemo(
    () => (new Date(localYear, localMonth, 1).getDay() + 6) % 7,
    [localYear, localMonth]
  );

  const employeeId = String(employee?.id ?? "");
  const baseStatusMap = attendanceData?.[employeeId] || attendanceData?.[employee?.id] || {};
  const baseDetailMap =
    attendanceDetailData?.[employeeId] || attendanceDetailData?.[employee?.id] || {};
  const isLocalCurrentMonth = localMonth === selectedMonth && localYear === selectedYear;
  const empStatusMap = isLocalCurrentMonth ? baseStatusMap : localAttendanceMap;
  const empDetailMap = isLocalCurrentMonth ? baseDetailMap : localAttendanceDetailMap;

  const employeeWorklogByDay = useMemo(() => {
    if (!employee?.id) return {};
    return readEmployeeWorklogMap(employee.id);
  }, [employee?.id, policyRefreshTick]);

  const displayStatusMap = useMemo(() => {
    if (!employee?.id) return empStatusMap;
    return buildMonthPolicyStatusMap({
      employee,
      year: localYear,
      month: localMonth,
      statusMap: empStatusMap,
      detailMap: empDetailMap,
      worklogByDay: employeeWorklogByDay,
      daysInMonth,
      loggedInEmployeeId,
    });
  }, [
    employee,
    localYear,
    localMonth,
    empStatusMap,
    empDetailMap,
    employeeWorklogByDay,
    daysInMonth,
    policyRefreshTick,
    loggedInEmployeeId,
    demoPolicyReady,
  ]);
  const statusOptions = useMemo(() => {
    const map = new Map();
    [
      ["PR", "Present"],
      ["LOP", "Loss of Pay"],
      ["WO", "Weekly Off"],
      ["PH", "Public Holiday"],
    ].forEach(([code, label]) => {
      const mappedCode = attendanceMapping?.[code] || code;
      map.set(mappedCode, label);
    });
    (leaveTypes || []).forEach((lt) => {
      if (!lt || lt.isActive === false) return;
      const rawCode = (lt.code || lt.shortCode || lt.id || "").toString().toUpperCase();
      if (!rawCode) return;
      const mappedCode = attendanceMapping?.[rawCode] || rawCode;
      if (!map.has(mappedCode)) {
        map.set(mappedCode, lt.name || lt.leaveName || rawCode);
      }
    });
    return Array.from(map.entries()).map(([code, label]) => ({ code, label }));
  }, [attendanceMapping, leaveTypes]);

  const codeColorMap = useMemo(() => {
    const m = {};
    (leaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || lt.id || "").toUpperCase();
      if (code && lt.color) m[code] = lt.color;
    });
    return m;
  }, [leaveTypes]);

  useEffect(() => {
    const fetchEmployeeMonthAttendance = async () => {
      if (!isOpen || !employee?.id) return;
      if (isLocalCurrentMonth) {
        setLocalAttendanceMap({});
        setLocalAttendanceDetailMap({});
        return;
      }
      try {
        setIsLoadingMonthData(true);
        const response = await attendanceService.getAttendanceHistory(
          localMonth + 1,
          localYear,
          1,
          500,
          "",
          [employee.id]
        );
        const statusMap = {};
        const detailMap = {};
        const records = Array.isArray(response?.data) ? response.data : [];
        records.forEach((record) => {
          const d = new Date(record?.date);
          if (isNaN(d.getTime())) return;
          const day = String(d.getDate());
          statusMap[day] = record?.status || "-";
          detailMap[day] = {
            status: record?.status || "-",
            checkIn:
              record?.checkIn ||
              record?.checkInTime ||
              record?.inTime ||
              record?.firstIn ||
              record?.firstPunchIn ||
              "--",
            checkOut:
              record?.checkOut ||
              record?.checkOutTime ||
              record?.outTime ||
              record?.lastOut ||
              record?.lastPunchOut ||
              "--",
            totalHours:
              record?.totalHours ||
              record?.workedHours ||
              record?.workingHours ||
              record?.totalWorkedHours ||
              "--",
            shiftAssigned:
              record?.shiftAssigned ||
              record?.shiftName ||
              record?.shift ||
              record?.assignedShift ||
              "--",
          };
        });
        setLocalAttendanceMap(statusMap);
        setLocalAttendanceDetailMap(detailMap);
      } catch (err) {
        console.error("Failed to load employee attendance calendar month data:", err);
        setLocalAttendanceMap({});
        setLocalAttendanceDetailMap({});
      } finally {
        setIsLoadingMonthData(false);
      }
    };
    fetchEmployeeMonthAttendance();
  }, [isOpen, employee?.id, isLocalCurrentMonth, localMonth, localYear]);

  const clampToCurrentMonthMax = (year, month) => {
    const now = new Date();
    const maxY = now.getFullYear();
    const maxM = now.getMonth();
    if (year > maxY || (year === maxY && month > maxM)) {
      return { y: maxY, m: maxM };
    }
    return { y: year, m: month };
  };

  const handleLocalMonthChange = (eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";
    if (!rawValue) return;
    const [start] = rawValue.split("/");
    if (!start) return;
    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;
    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) return;
    const clamped = clampToCurrentMonthMax(parsedYear, Math.min(Math.max(parsedMonth, 0), 11));
    setLocalYear(clamped.y);
    setLocalMonth(clamped.m);
    setSelectedDay(getDefaultSelectedDay(clamped.y, clamped.m));
  };

  const canGoNextMonth = useMemo(() => {
    const n = new Date();
    return (
      localYear < n.getFullYear() || (localYear === n.getFullYear() && localMonth < n.getMonth())
    );
  }, [localYear, localMonth]);

  const canGoPrevMonth = localYear > 1900 || (localYear === 1900 && localMonth > 0);

  const handlePrevMonth = () => {
    if (!canGoPrevMonth) return;
    if (localMonth === 0) {
      setLocalYear((y) => y - 1);
      setLocalMonth(11);
    } else {
      setLocalMonth((m) => m - 1);
    }
    setSelectedDay(getDefaultSelectedDay(localMonth === 0 ? localYear - 1 : localYear, localMonth === 0 ? 11 : localMonth - 1));
  };

  const handleNextMonth = () => {
    if (!canGoNextMonth) return;
    let y = localYear;
    let mo = localMonth;
    if (mo === 11) {
      y += 1;
      mo = 0;
    } else {
      mo += 1;
    }
    const c = clampToCurrentMonthMax(y, mo);
    setLocalYear(c.y);
    setLocalMonth(c.m);
    setSelectedDay(getDefaultSelectedDay(c.y, c.m));
  };

  const now = new Date();
  const currentMonthActiveDay =
    localYear === now.getFullYear() && localMonth === now.getMonth()
      ? now.getDate()
      : null;
  const activeDay = selectedDay != null ? selectedDay : currentMonthActiveDay;

  const selectedDayStatus =
    activeDay != null
      ? displayStatusMap?.[String(activeDay)] ?? displayStatusMap?.[activeDay] ?? "-"
      : "-";
  const selectedDateLabel =
    activeDay != null
      ? `${String(activeDay).padStart(2, "0")}-${String(localMonth + 1).padStart(2, "0")}-${localYear}`
      : "-";
  const selectedDateIso =
    activeDay != null
      ? `${localYear}-${String(localMonth + 1).padStart(2, "0")}-${String(activeDay).padStart(2, "0")}`
      : null;

  const isSelectedDateBeforeToday = useMemo(() => {
    if (!selectedDateIso) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(selectedDateIso);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }, [selectedDateIso]);

  const canEditShiftOverride =
    !isLocked && !!selectedDateIso && !!employee?.id && !isSelectedDateBeforeToday;

  useEffect(() => {
    if (!isOpen || !employee?.id || !selectedDateIso) {
      setDayShiftOverrideId("");
      return;
    }
    const ov = getShiftOverrideForDay(employee.id, selectedDateIso);
    setDayShiftOverrideId(ov?.shiftId || "");
  }, [isOpen, employee?.id, selectedDateIso, policyRefreshTick]);

  const navbarDayWorklog = useMemo(() => {
    if (!selectedDateIso || !employee?.id) return null;
    const row = readEmployeeWorklog(employee.id, selectedDateIso);
    if (!row) return null;
    return { inMs: row.inMs, outMs: row.outMs };
  }, [selectedDateIso, employee?.id, policyRefreshTick]);

  const selectedDayDetails =
    activeDay != null
      ? empDetailMap?.[String(activeDay)] || empDetailMap?.[activeDay] || {}
      : {};
  const selectedDayDetailsWithNavbarLog = useMemo(() => {
    const requestShiftName = resolveApprovedChangeShiftName(employee?.id, selectedDateIso);
    const currentShift =
      requestShiftName ||
      selectedDayDetails?.shiftAssigned ||
      selectedDayDetails?.shiftName ||
      selectedDayDetails?.assignedShift;
    const needsDefaultShift =
      !currentShift ||
      String(currentShift).trim() === "" ||
      String(currentShift).trim() === "--";
    const defaultShiftName = needsDefaultShift ? resolveDefaultShiftName() : null;

    const base = {
      ...selectedDayDetails,
      ...(currentShift && String(currentShift).trim() !== "--"
        ? { shiftAssigned: currentShift }
        : defaultShiftName
        ? { shiftAssigned: defaultShiftName }
        : {}),
    };

    if (!navbarDayWorklog) return base;

    const checkIn = formatClockFromMs(navbarDayWorklog.inMs);
    const checkOut =
      navbarDayWorklog.outMs != null ? formatClockFromMs(navbarDayWorklog.outMs) : "--";
    const totalHours =
      navbarDayWorklog.outMs != null
        ? formatElapsedHMS(navbarDayWorklog.inMs, navbarDayWorklog.outMs)
        : "--";
    return {
      ...base,
      checkIn,
      checkOut,
      totalHours,
    };
  }, [navbarDayWorklog, selectedDayDetails, employee?.id, selectedDateIso]);

  const openStatusEditModal = (dayOverride = null, statusOverride = null) => {
    const candidateDay = dayOverride ?? selectedDay;
    const normalizedDay = Number(candidateDay);
    if (!Number.isFinite(normalizedDay) || normalizedDay < 1) return;
    const targetDay = Math.trunc(normalizedDay);
    const fallbackStatus =
      displayStatusMap?.[String(targetDay)] ?? displayStatusMap?.[targetDay] ?? "-";
    const current = String(statusOverride ?? fallbackStatus).trim();
    const parts = current.includes("/") ? current.split("/") : [current, current];
    setFirstHalfStatus((parts[0] || "PR").trim() || "PR");
    setSecondHalfStatus((parts[1] || parts[0] || "PR").trim() || "PR");
    setSelectedDay(targetDay);
    setShowStatusEditModal(true);
    setShowDayActionMenu(false);
  };

  const openRegularizationModal = () => {
    if (selectedDay == null || selectedDay < 1) return;
    const dateIso = selectedDateIso ? clampDateIsoToToday(selectedDateIso) : null;
    if (dateIso && isFutureDateIso(dateIso)) return;
    setShowRegularizationModal(true);
    setShowDayActionMenu(false);
  };

  const handleRegulariseChecksSubmit = (formData) => {
    if (!employee?.id || !formData?.date) return;
    const year = formData.year ?? parseInt(formData.date.split("-")[0], 10);
    const month =
      formData.month ?? parseInt(formData.date.split("-")[1], 10) - 1;
    const day = formData.day ?? parseInt(formData.date.split("-")[2], 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return;

    const checkIn = formData.inTime?.trim() || "--";
    const checkOut = formData.outTime?.trim() || "--";
    const totalHours = formData.totalHours?.trim() || "--";
    const dayKey = String(day);
    const sameCalendarMonth = year === localYear && month === localMonth;

    if (sameCalendarMonth && typeof onSaveRegularizationTimes === "function") {
      onSaveRegularizationTimes(employee, {
        day,
        year,
        month,
        checkIn,
        checkOut,
        totalHours,
      });
    }
    if (!sameCalendarMonth) {
      setLocalAttendanceDetailMap((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev?.[dayKey] || {}),
          checkIn,
          checkOut,
          totalHours,
        },
      }));
    }

    reloadRegularisationSessionLog();

    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    const requestEntry = {
      requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      panelKey: "review",
      actionLabel: "Regularise checks",
      employeeId: String(employee.id),
      userId: currentUser?.id || null,
      userName: employee?.name || employee?.user?.name || currentUser?.name || "Employee",
      status: "Pending",
      submittedAt: Date.now(),
      formData,
      pendingWith: "Reporting Manager",
      workflowTrail: [
        {
          status: "Pending",
          actor: currentUser?.name || "Employee",
          actedAt: Date.now(),
        },
      ],
    };
    try {
      const raw = localStorage.getItem("adminQuickActionRequests");
      const existing = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(existing) ? existing : [];
      localStorage.setItem(
        "adminQuickActionRequests",
        JSON.stringify([requestEntry, ...list])
      );
    } catch {
      /* ignore */
    }

    setShowRegularizationModal(false);
  };

  const handleSaveEditedStatus = () => {
    if (selectedDay == null || selectedDay < 1) return;
    const combinedStatus = combineStatuses(firstHalfStatus, secondHalfStatus);
    const dayKey = String(selectedDay);

    if (isLocalCurrentMonth && typeof onEditAttendanceStatus === "function") {
      onEditAttendanceStatus(employee, selectedDay, combinedStatus, localYear, localMonth);
    }
    if (!isLocalCurrentMonth) {
      setLocalAttendanceMap((prev) => ({ ...prev, [dayKey]: combinedStatus }));
      setLocalAttendanceDetailMap((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev?.[dayKey] || {}),
          ...(empDetailMap?.[dayKey] || {}),
          status: combinedStatus,
        },
      }));
    }
    setShowStatusEditModal(false);
  };

  const handleCancelForm = () => {
    onClose?.();
  };

  const handleSaveForm = () => {
    // Day-level edits are already applied through existing status-save flow.
    // This keeps behavior as-is while providing explicit form actions.
    onClose?.();
  };

  if ((!isOpen && !embedded) || !employee) return null;

  return (
    <div
      className={
        embedded
          ? "h-full min-h-0"
          : "fixed inset-0 bg-black/50 z-[10030] flex items-center justify-center p-4"
      }
    >
      <div
        className={`relative ${
          embedded ? "z-[1] w-full h-full" : "z-[10031] w-[min(85.5vw,973px)] h-[min(74.1vh,684px)]"
        } rounded-xl shadow-xl border flex flex-col overflow-visible ${
          darkMode
            ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0]"
            : "bg-white border-gray-300 text-slate-900"
        }`}
      >
        <div
          className={`shrink-0 px-[calc(1.25rem*0.95)] py-[calc(0.75rem*0.95)] border-b flex items-start justify-between gap-[calc(0.75rem*0.95)] ${
            darkMode ? "border-[#3C3C3C]" : "border-gray-200"
          }`}
        >
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Calendar</h3>
            {demoPolicyReady && loggedInEmployeeId && String(employee?.id) === loggedInEmployeeId && (
              <p
                className={`mt-1 text-[10px] leading-snug max-w-md ${
                  darkMode ? "text-gray-400" : "text-slate-500"
                }`}
                title={`${demoTestGuide.examplePresent}. ${demoTestGuide.exampleLop}.`}
              >
                Auto attendance: {demoTestGuide.policyName} · {demoTestGuide.todayBehavior}
              </p>
            )}
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded ${
                darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"
              }`}
              aria-label="Close attendance calendar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="shrink-0 px-[calc(1.25rem*0.95)] py-[calc(0.75rem*0.95)] border-b border-gray-200 dark:border-[#3C3C3C] relative z-20 overflow-visible">
          <div className="flex items-center justify-between gap-[calc(0.75rem*0.95)] flex-wrap">
            <div className="text-sm font-medium">Month</div>
            <div className="flex items-center gap-[calc(0.5rem*0.95)] justify-end min-w-0">
              <button
                type="button"
                onClick={handlePrevMonth}
                disabled={!canGoPrevMonth}
                className={`shrink-0 p-2 rounded border ${
                  !canGoPrevMonth
                    ? "opacity-40 cursor-not-allowed"
                    : darkMode
                    ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="relative z-20 flex-none">
                <DatePicker
                  monthYearOnly
                  showMonthName
                  value={getMonthRangeValue(localYear, localMonth)}
                  onChange={handleLocalMonthChange}
                  max={new Date()}
                  darkMode={darkMode}
                  dropdownAlign="auto"
                  dropdownClassName="!z-[10050] shadow-2xl"
                  className="z-10 !w-auto min-w-[142.5px]"
                />
              </div>
              <button
                type="button"
                onClick={handleNextMonth}
                disabled={!canGoNextMonth}
                className={`shrink-0 p-2 rounded border ${
                  !canGoNextMonth
                    ? "opacity-40 cursor-not-allowed"
                    : darkMode
                    ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 lg:grid-cols-3 gap-0">
          <div className="lg:col-span-2 border-r border-gray-200 dark:border-[#3C3C3C] p-[calc(1rem*0.95)]">
            {isLoadingMonthData && (
              <div className={`mb-3 text-xs ${darkMode ? "text-gray-300" : "text-slate-600"}`}>
                Loading attendance...
              </div>
            )}
            <div className="grid grid-cols-7 gap-[calc(0.5rem*0.95)] mb-[calc(0.5rem*0.95)]">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name) => (
                <div
                  key={name}
                  className={`text-[10.5px] font-semibold text-center ${
                    darkMode ? "text-gray-300" : "text-slate-700"
                  }`}
                >
                  {name}
                </div>
              ))}
            </div>

            <div
              className="grid grid-cols-7 gap-[calc(0.5rem*0.95)]"
              onClick={() => {
                setSelectedDay(null);
              }}
            >
              {Array.from({ length: dayOffset }).map((_, idx) => (
                <div
                  key={`blank-${idx}`}
                  className="min-h-[56px]"
                  onClick={() => setSelectedDay(null)}
                  role="presentation"
                />
              ))}

              {Array.from({ length: daysInMonth }, (_, idx) => {
                const day = idx + 1;
                const status =
                  displayStatusMap?.[String(day)] ?? displayStatusMap?.[day] ?? "-";
                const isToday =
                  localYear === now.getFullYear() &&
                  localMonth === now.getMonth() &&
                  day === now.getDate();
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedDay(day);
                    }}
                    className={`min-h-[53.2px] rounded-lg border p-1.5 flex flex-col text-left transition-colors ${
                      isToday
                        ? darkMode
                          ? "border-cyan-400 bg-cyan-950/25"
                          : "border-cyan-500 bg-cyan-50"
                        : darkMode
                        ? "border-[#3C3C3C] hover:bg-[#343434]"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`text-[9.5px] font-semibold leading-none ${
                        darkMode ? "text-gray-400" : "text-slate-500"
                      }`}
                    >
                      {day}
                    </span>
                    <div
                      className={`mt-0.5 flex min-h-[30.4px] flex-1 flex-col items-center justify-center rounded px-0.5 py-0.5 ${
                        darkMode ? "bg-[#1f1f1f]" : "bg-[#fafafa]"
                      }`}
                    >
                      {renderColoredStatusContent(
                        status,
                        attendanceMapping,
                        codeColorMap,
                        darkMode,
                        "text-[8.6px]"
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-[calc(1rem*0.95)] space-y-[calc(0.75rem*0.95)]">
            <h4 className="text-sm font-semibold">Day details</h4>
            <div
              className={`rounded-lg border p-[calc(0.75rem*0.95)] text-xs space-y-1 ${
                darkMode ? "border-[#3C3C3C] bg-[#262626]" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div>
                <span className="font-medium">Date:</span> {selectedDateLabel}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0">
                <span className="font-medium">Status:</span>
                {renderColoredStatusContent(
                  selectedDayStatus,
                  attendanceMapping,
                  codeColorMap,
                  darkMode,
                  "text-xs"
                )}
              </div>
              <div>
                <span className="font-medium">Check-in:</span> {selectedDayDetailsWithNavbarLog.checkIn || "--"}
              </div>
              <div>
                <span className="font-medium">Check-out:</span> {selectedDayDetailsWithNavbarLog.checkOut || "--"}
              </div>
              <div>
                <span className="font-medium">Total hours:</span> {selectedDayDetailsWithNavbarLog.totalHours || "--"}
              </div>
              <div>
                <span className="font-medium">Shift assigned:</span>{" "}
                {selectedDayDetailsWithNavbarLog.shiftAssigned || "--"}
              </div>
            </div>

            {shiftOptions.length > 0 && (
              <div
                className={`rounded-lg border p-[calc(0.75rem*0.95)] text-xs ${
                  darkMode ? "border-[#3C3C4C] bg-[#1f1f1f]" : "border-amber-200 bg-amber-50/80"
                }`}
              >
                <p className="font-semibold text-[11px] uppercase tracking-wide mb-1">
                  Shift override
                </p>
                <p className={`mb-2 text-[10px] ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Set the roster shift for this day before check-in. Policy timing (grace, late
                  rules) uses this shift instead of the policy default.
                </p>
                <select
                  disabled={!canEditShiftOverride}
                  value={dayShiftOverrideId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setDayShiftOverrideId(id);
                    if (!employee?.id || !selectedDateIso) return;
                    if (!id) {
                      clearShiftOverrideForDay(employee.id, selectedDateIso);
                      setPolicyRefreshTick((n) => n + 1);
                      toast.success("Using policy default shift");
                      return;
                    }
                    const sh = shiftOptions.find((s) => String(s.id) === id);
                    if (sh) {
                      setShiftOverrideForDay(employee.id, sh.id, sh.name, selectedDateIso);
                      setPolicyRefreshTick((n) => n + 1);
                      toast.success(`Shift override: ${sh.name}`);
                    }
                  }}
                  className={`w-full rounded border px-2 py-1.5 text-xs ${
                    darkMode
                      ? "border-[#3C3C4C] bg-[#2C2C2C] text-gray-100"
                      : "border-gray-300 bg-white text-gray-900"
                  } ${!canEditShiftOverride ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <option value="">Policy default shift</option>
                  {shiftOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.shiftType || "fixed"})
                    </option>
                  ))}
                </select>
                {!canEditShiftOverride && (
                  <p className="mt-1 text-[10px] text-gray-500">
                    {isLocked
                      ? "Month is locked."
                      : isSelectedDateBeforeToday
                      ? "Past days are view-only."
                      : "Select a day to override shift."}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Actions for selected day</h4>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDayActionMenu((prev) => !prev)}
                  className={`p-1.5 rounded border ${
                    darkMode
                      ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                  title="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {showDayActionMenu && (
                  <div
                    className={`absolute right-0 top-[2.1375rem] min-w-[190px] rounded border shadow-lg z-[10040] ${
                      darkMode
                        ? "bg-[#2C2C2C] border-[#3C3C3C]"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {/* <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        openStatusEditModal();
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium ${
                        isLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "hover:bg-[#3C3C3C]"
                          : "hover:bg-gray-100"
                      }`}
                      title={isLocked ? "Attendance is locked for this month" : "Edit attendance status"}
                    >
                      Edit attendance status
                    </button> */}
                    {canApplyLeave && (
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        onApplyLeave?.(employee);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        isLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                          : "border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      Apply Leave
                    </button>
                    )}
                    {canRegularise && (
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        openRegularizationModal();
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        isLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                          : "border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      Regularise checks
                    </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <p className={`text-[10.5px] ${darkMode ? "text-gray-400" : "text-slate-600"}`}>
              Tip: Use the ⋮ menu to edit status, apply leave, or regularise check-in and check-out for the
              selected day.
            </p>
          </div>
        </div>
        {!embedded && (
          <div
            className={`shrink-0 px-[calc(1rem*0.95)] py-[calc(0.75rem*0.95)] border-t flex items-center justify-end gap-[calc(0.5rem*0.95)] ${
              darkMode ? "border-[#3C3C3C] bg-[#2A2A2A]" : "border-gray-200 bg-white"
            }`}
          >
            <button
              type="button"
              onClick={handleCancelForm}
              className={`px-3 py-1.5 rounded text-sm border ${
                darkMode
                  ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                  : "border-gray-300 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveForm}
              className="px-3 py-1.5 rounded text-sm rh-btn-primary hover:opacity-95 flex items-center"
            >
              <Check className="w-4 h-4 mr-1" />
              Save
            </button>
          </div>
        )}
      </div>
      <RegulariseChecksRequestModal
        isOpen={showRegularizationModal}
        onClose={() => setShowRegularizationModal(false)}
        darkMode={darkMode}
        employeeId={employee?.id}
        employeeName={employee?.name || employee?.user?.name || employee?.employeeName || "Employee"}
        initialDateIso={selectedDateIso ? clampDateIsoToToday(selectedDateIso) : null}
        loggedInEmployeeId={loggedInEmployeeId}
        onSubmit={handleRegulariseChecksSubmit}
        zIndexClass="z-[10060]"
      />
      {showStatusEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10060]">
          <div
            className={`w-[min(87.4vw,399px)] rounded-lg shadow-xl p-[calc(1rem*0.95)] ${
              darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">
                Edit attendance status - Day {selectedDay}
              </h4>
              <button
                type="button"
                onClick={() => setShowStatusEditModal(false)}
                className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">First half status</label>
                <select
                  value={firstHalfStatus}
                  onChange={(e) => setFirstHalfStatus(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"
                  }`}
                >
                  {statusOptions.map((status) => (
                    <option key={`fh-${status.code}`} value={status.code}>
                      {status.code} - {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Second half status</label>
                <select
                  value={secondHalfStatus}
                  onChange={(e) => setSecondHalfStatus(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"
                  }`}
                >
                  {statusOptions.map((status) => (
                    <option key={`sh-${status.code}`} value={status.code}>
                      {status.code} - {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`rounded p-2 text-xs ${darkMode ? "bg-[#1F1F1F]" : "bg-gray-100"}`}>
                Result: <span className="font-semibold">{combineStatuses(firstHalfStatus, secondHalfStatus)}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowStatusEditModal(false)}
                className={`px-3 py-1.5 rounded text-sm ${
                  darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditedStatus}
                className="px-3 py-1.5 rounded text-sm rh-btn-primary hover:opacity-95 flex items-center"
              >
                <Check className="w-4 h-4 mr-1" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAttendanceCalendarPopup;
